import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import moment from 'moment';
import { useState } from 'react';

function UserList({ users, onToggleActive, onDelete, totalUsers, hasSearch }) {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const openMenu = (event, user) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const getLastSeenSourceLabel = (source) => {
    const labels = {
      login: 'Kaynak: giriş',
      prayer: 'Kaynak: namaz kaydı',
      fasting: 'Kaynak: oruç kaydı',
      support: 'Kaynak: destek hareketi',
      activity: 'Kaynak: aktivite',
    };

    return labels[source] || 'Kaynak: bilinmiyor';
  };

  const getSubscriptionChip = (user) => {
    const type = user.subscription?.type || 'free';
    const status = user.subscription?.status || 'none';
    const plan = user.subscription?.plan;

    const colorMap = {
      pro: { bg: '#FEF3C7', color: '#B45309' },
      standard: { bg: '#DBEAFE', color: '#1D4ED8' },
      free: { bg: '#F8FAFC', color: '#475569' },
    };

    const palette = colorMap[type] || colorMap.free;
    const label = type === 'free'
      ? 'Free'
      : `${type === 'pro' ? 'Pro' : 'Standart'}${plan ? ` · ${plan}` : ''}${status === 'active' ? '' : ` · ${status}`}`;

    return (
      <Chip
        size="small"
        label={label}
        sx={{
          backgroundColor: palette.bg,
          color: palette.color,
          fontWeight: 700,
          borderRadius: '999px',
          maxWidth: '100%',
        }}
      />
    );
  };

  const proUsersOnPage = users.filter((user) => user.subscription?.type === 'pro').length;

  return (
    <Box
      sx={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid #E5E7EB',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
            Kullanıcı Listesi
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
            {hasSearch ? 'Arama sonucuna göre filtrelenen kullanıcılar listeleniyor.' : 'Tüm kullanıcı kayıtları en yeniden eskiye sıralanıyor.'}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Chip
            icon={<PeopleOutlineIcon />}
            label={`${totalUsers} kayıt`}
            sx={{
              backgroundColor: '#F8FAFC',
              color: '#475569',
              fontWeight: 600,
              borderRadius: '10px',
            }}
          />
          <Chip
            icon={<WorkspacePremiumOutlinedIcon />}
            label={`${proUsersOnPage} Pro / sayfa`}
            sx={{
              backgroundColor: '#FEF3C7',
              color: '#B45309',
              fontWeight: 600,
              borderRadius: '10px',
            }}
          />
        </Stack>
      </Stack>

      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Kullanıcı</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Abonelik</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Aktivite</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Etkileşim</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Kayıt Tarihi</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Durum</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Aksiyon</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 8 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#111827', mb: 1 }}>
                      Kullanıcı bulunamadı
                    </Typography>
                    <Typography sx={{ color: '#6B7280' }}>
                      Arama kriterini temizleyip tekrar deneyin.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : users.map((user) => (
              <TableRow
                key={user._id}
                sx={{
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    transition: 'all 0.2s',
                  },
                }}
              >
                <TableCell sx={{ py: 2, minWidth: 260 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        backgroundColor: '#DBEAFE',
                        color: '#2563EB',
                        fontWeight: 700,
                      }}
                    >
                      {(user.name || user.username || user.email || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                        {user.name || user.username || 'İsimsiz kullanıcı'}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell sx={{ py: 2, minWidth: 190 }}>
                  <Stack spacing={1}>
                    <Box sx={{ maxWidth: '100%' }}>{getSubscriptionChip(user)}</Box>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      {user.subscription?.endDate
                        ? `Bitiş: ${moment(user.subscription.endDate).format('DD MMM YYYY')}`
                        : 'Abonelik bitişi yok'}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ py: 2, minWidth: 210 }}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: '#64748B' }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                        {user.recent?.lastSeenAt
                          ? `Son kaydedilen işlem · ${moment(user.recent.lastSeenAt).fromNow()}`
                          : 'Kayıtlı işlem yok'}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
                      {getLastSeenSourceLabel(user.recent?.lastSeenSource)}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      {user.metrics?.totalPrayers || 0} namaz · {user.metrics?.totalFastings || 0} oruç
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      {user.metrics?.totalKazaDebt > 0
                        ? `${user.metrics.totalKazaDebt} kaza borcu`
                        : `${user.metrics?.kazaRecords || 0} kaza kaydı · ${user.metrics?.totalKazaPrayed || 0} kılınan`
                      } · {user.metrics?.openSupportTickets || 0} açık destek
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ py: 2, minWidth: 220 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: '#2563EB' }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                        Seviye {user.metrics?.level || 1} · {user.metrics?.totalXP || 0} XP
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      {user.metrics?.familyCount || 0} aile · {user.metrics?.friendCount || 0} arkadaş
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      {user.metrics?.activeHatimProgress || 0}% aktif hatim · {user.metrics?.completedHatims || 0} tamamlanan
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ fontSize: '14px', color: '#475569' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                    {moment(user.registerDate || user.createdAt).format('DD MMM YYYY')}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                    {moment(user.registerDate || user.createdAt).format('HH:mm')}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={user.isActive !== false ? 'Aktif' : 'Engelli'}
                      sx={{
                        backgroundColor: user.isActive !== false ? '#DCFCE7' : '#FEE2E2',
                        color: user.isActive !== false ? '#15803D' : '#B91C1C',
                        fontWeight: 700,
                        fontSize: '12px',
                        borderRadius: '999px',
                      }}
                      size="small"
                    />
                    {(user.metrics?.pendingRequests || 0) > 0 ? (
                      <Chip
                        size="small"
                        label={`${user.metrics.pendingRequests} bekleyen istek`}
                        sx={{
                          backgroundColor: '#FFF7ED',
                          color: '#C2410C',
                          fontWeight: 700,
                          borderRadius: '999px',
                        }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>

                <TableCell align="right">
                  <Tooltip title="İşlemler">
                    <IconButton
                      size="small"
                      onClick={(event) => openMenu(event, user)}
                      sx={{
                        color: '#475569',
                        border: '1px solid #E5E7EB',
                        borderRadius: '10px',
                        '&:hover': { backgroundColor: '#F8FAFC' },
                      }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: 200,
            boxShadow: '0 10px 32px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (!selectedUser) return;
            navigate(`/users/${selectedUser._id}`);
            closeMenu();
          }}
          sx={{ gap: 1.25 }}
        >
          <VisibilityIcon fontSize="small" />
          Detayları Gör
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedUser) return;
            onToggleActive(selectedUser._id, selectedUser.isActive);
            closeMenu();
          }}
          sx={{
            gap: 1.25,
            color: selectedUser?.isActive !== false ? '#B91C1C' : '#15803D',
          }}
        >
          {selectedUser?.isActive !== false ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          {selectedUser?.isActive !== false ? 'Kullanıcıyı Engelle' : 'Kullanıcıyı Aktif Et'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedUser) return;
            onDelete(selectedUser._id);
            closeMenu();
          }}
          sx={{ gap: 1.25, color: '#B91C1C' }}
        >
          <DeleteIcon fontSize="small" />
          Kullanıcıyı Sil
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default UserList;
